import { Injectable } from '@angular/core';
import { MemberLookupResult, PurchasePackageFromPOViewModel, RegistrationPackageViewModel } from '../_models/generatedModels';
import Enumerable from 'linq';
import { cloneDeep } from 'lodash';

@Injectable()
export class RegistrationHelperService {

  private nextRowNumber: number = 1;
  private _currencyCode: string;
  private _conferencePurchaseOrderId: number | null = null;

  private registrantPurchases: PurchasePackageFromPOViewModel[];

  constructor() { }

  get currencyCode(): string {
    return this._currencyCode;
  }

  private set currencyCode(value: string) {
    this._currencyCode = value;
  }

  get conferencePurchaseOrderId(): number | null {
    return this._conferencePurchaseOrderId;
  }

  private set conferencePurchaseOrderId(value: number | null) {
    this._conferencePurchaseOrderId = value;
  }

  public initialize(currencyCode: string) {
    this.registrantPurchases = [];
    this.currencyCode = currencyCode;
  }

  public initializeExistingPO(currencyCode: string, conferencePurchaseOrderId: number, existingRegistrants: PurchasePackageFromPOViewModel[]) {
    this.registrantPurchases = cloneDeep(existingRegistrants);
    this.currencyCode = currencyCode;
    this.conferencePurchaseOrderId = conferencePurchaseOrderId;
    this.nextRowNumber = this.registrantPurchases.reduce((max, current) => Math.max(max, current.tempRowNumber || 0), 0) + 1;
  }

  public getNextRowNumber() {
    return this.nextRowNumber;
  }

  public getRegistrantPurchases() {
    return this.registrantPurchases;
  }

  public getRegistrantPurchase(rowNumber: number) {
    return this.registrantPurchases.find((x) => x.tempRowNumber === rowNumber);
  }

  public addRegistrantPurchase(registrantPurchase: PurchasePackageFromPOViewModel) {
    this.registrantPurchases.push(registrantPurchase);
    this.nextRowNumber++;
  }

  public updateRegistrantPurchase(registrantPurchase: PurchasePackageFromPOViewModel) {
    const index = this.registrantPurchases.findIndex(item => item.tempRowNumber === registrantPurchase.tempRowNumber);

    if (index !== -1) {
      this.registrantPurchases[index] = registrantPurchase;
    }
  }

  public removeRegistrantPurchase(rowNumber: number) {
    const index = this.registrantPurchases.findIndex(item => item.tempRowNumber === rowNumber);

    if (index !== -1) {
        this.registrantPurchases.splice(index, 1);
    }
  }

  public isEmailDuplicate(email: string, curentRowNumber: number): boolean {
    return this.registrantPurchases.some((x) => x.email.toLowerCase() == email.toLowerCase() && x.tempRowNumber != curentRowNumber);
  }

  public getFirstCurrencyCode(): string {
    if (this.registrantPurchases.length > 0) {
      return this.registrantPurchases[0].package.currencyCode;
    }
    return null;
  }

  public calculateRegistrantPrice(pckg: RegistrationPackageViewModel, isMember: boolean, purchaseCEUs: boolean, membershipLookupResult: MemberLookupResult, promoDiscount: number, promoDiscountAmount: number): number {
    var actualPrice = 0;
    if (isMember || !pckg.memberPricing) {
      actualPrice = pckg.price;
    } else {
      actualPrice = pckg.nonMemberPrice;
    }

    if (purchaseCEUs && (isMember || !pckg.memberPricing)) {
      actualPrice += pckg.ceuPrice;
    } else if (purchaseCEUs && !isMember) {
      actualPrice += pckg.ceuNonMemberPrice;
    }

    if (membershipLookupResult?.discountApplied) {
      actualPrice = actualPrice - actualPrice * membershipLookupResult.discountPercentage;
    }

    if (promoDiscount > 0) {
      actualPrice = actualPrice - actualPrice * (promoDiscount / 100);
    } else if (promoDiscountAmount > 0) {
      actualPrice = actualPrice - promoDiscountAmount;
    } else {
      actualPrice = actualPrice;
    }

    var addonPrice = Enumerable.from(pckg.addOns)
      .where((x) => x.selected)
      .sum((x) => x.price);
    actualPrice += addonPrice;

    var eventAddonPrice = Enumerable.from(pckg.eventAddons)
      .where((x) => x.selected)
      .sum((x) => {
        if (x.memberPricing && isMember) return x.memberPrice;
        return x.price;
      });

    var count = Enumerable.from(pckg.eventAddons)
      .where((x) => x.selected)
      .count();

    if (pckg.eventAddonDiscountQuantityThreshold && count >= pckg.eventAddonDiscountQuantityThreshold) {
      var discountAmount = eventAddonPrice * pckg.eventAddonDiscountAmount;
      eventAddonPrice = eventAddonPrice - discountAmount;
    }

    actualPrice += eventAddonPrice;

    return actualPrice;
  }
}
